import { useTranslation } from 'next-i18next'

import {
  PURCHASE_TYPES,
  PurchaseType,
} from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  getEarnCreditTagFor3PVendor,
  getUnitForPerServing,
  shouldShowPricePerUnit,
} from '@src/quince-site-common/src/helpers/productHelper'
import { getPriceStyleKey } from '@src/quince-site-common/src/utils/festivalOffersUtility'

export const STYLE_KEY_MAP = {
  festival: 'festivalOffer' as const,
  subscription: 'subscriptionOffer' as const,
  default: 'default' as const,
}

export type OfferStyleKey = (typeof STYLE_KEY_MAP)[keyof typeof STYLE_KEY_MAP]

export const DIFFERENCE_TO_IGNORE = 0.1

export interface ProductInfoPriceDSProps {
  product?: Product
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  showDiscount?: boolean
  pricePerServing?: number
  traditionalPricePerServing?: number
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  hideSavings?: boolean
  showNewPdpDesignV2?: boolean
  isFinalSaleProduct?: boolean
  isB2BFinalSaleProduct?: boolean
  formattedSubscriptionPrice?: string
  purchaseOption?: PurchaseType
  updatedDesignForV2?: boolean
  isVariantThirdParty?: boolean
  selectedOptions?: ProductVariantOption[]
  brandName?: string
}

// Derives the render flags for ProductInfoPriceDS; telemetry stays in the orchestrator.
export function usePriceDisplayState(props: ProductInfoPriceDSProps) {
  const {
    product,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    savingPercentage,
    formattedFestivalOfferPrice,
    showDiscount = true,
    pricePerServing,
    traditionalPricePerServing,
    hideSavings,
    showNewPdpDesignV2,
    purchaseOption,
    isVariantThirdParty,
    selectedOptions,
    brandName,
  } = props

  const { t } = useTranslation('common')
  const isDiscountLabelPresent =
    showDiscount && formattedTraditionalPrice
      ? Number(formattedTraditionalPrice) - Number(formattedSalePrice) > DIFFERENCE_TO_IGNORE
      : false
  const showPricePerUnit = shouldShowPricePerUnit(product)
  const hasPerUnitPricing = showPricePerUnit && !!pricePerServing && !!traditionalPricePerServing
  const perUnitSavingsPercentage = hasPerUnitPricing
    ? productHelper.getSavingPercentage(traditionalPricePerServing, pricePerServing)
    : ''

  const showBestMarketValue = isVariantThirdParty && !!brandName

  const showSavingPercentage = !!(
    !formattedFestivalOfferPrice &&
    !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
    formattedTraditionalPrice &&
    savingPercentage &&
    isDiscountLabelPresent &&
    !hasPerUnitPricing &&
    !hideSavings &&
    !showBestMarketValue
  )

  const showPerUnitSavingsPercentage = !!(
    perUnitSavingsPercentage &&
    !formattedFestivalOfferPrice &&
    !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
    !hideSavings &&
    !showBestMarketValue
  )
  const perUnitLabel = getUnitForPerServing(product)
  const unitForPerServing = perUnitLabel ? t(perUnitLabel) : ''
  const offerStyleKey =
    STYLE_KEY_MAP[
      getPriceStyleKey(
        !!formattedFestivalOfferPrice,
        !!formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE,
      )
    ]
  const applicableProductTags = productHelper.getApplicableTags({
    product,
    selectedOptions: selectedOptions ?? [],
    productTags: product?.tags,
  })
  const earnCreditTagFor3PVendor = isVariantThirdParty ? getEarnCreditTagFor3PVendor(applicableProductTags) : undefined

  // Offer-value selection requires a formatted subscription price...
  const isSubscriptionOffer = !!formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE
  // ...whereas the per-unit offer row uses this raw subscribe check WITHOUT requiring one, matching legacy.
  const isSubscribePurchase = purchaseOption === PURCHASE_TYPES.SUBSCRIBE

  return {
    showNewPdpDesignV2,
    isDiscountLabelPresent,
    hasPerUnitPricing,
    perUnitSavingsPercentage,
    showBestMarketValue,
    showSavingPercentage,
    showPerUnitSavingsPercentage,
    unitForPerServing,
    offerStyleKey,
    earnCreditTagFor3PVendor,
    isSubscriptionOffer,
    isSubscribePurchase,
  }
}
